import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { getLang, setLang } from "../i18n";
import FlashTicker from "./FlashTicker";
import QRCode from "qrcode";

// Backend root (NOT /api). Fallback for local dev.
const API_ROOT =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
  (window.location.hostname === "localhost" ? "http://localhost:5000" : window.location.origin);


/**
 * Builds a correct media URL for <img>, <video>, <audio>.
 * - If API returns absolute URL (http/https), return as-is.
 * - If API returns /uploads/... or uploads/..., prefix API_ROOT once.
 * - Normalize Windows paths (backslashes) to URL format.
 */
 function toMediaUrl(u) {
  if (!u) return "";
  const clean = String(u).replace(/\\/g, "/").trim();

  // If absolute URL but points to localhost/127.0.0.1, rewrite to current API_ROOT
  if (clean.startsWith("http://") || clean.startsWith("https://")) {
    try {
      const x = new URL(clean);
      if (x.hostname === "localhost" || x.hostname === "127.0.0.1") {
        return `${API_ROOT}${x.pathname}`; // keep /uploads/... path, replace host
      }
    } catch {}
    return clean; // normal absolute URL
  }

  if (clean.startsWith("/")) return `${API_ROOT}${clean}`;
  return `${API_ROOT}/${clean}`;
}


function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function useLockBodyScroll(open) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);
}

function Modal({ open, title, onClose, children }) {
  useLockBodyScroll(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div style={styles.modalOverlay} onMouseDown={onClose}>
      <div style={styles.modalCard} onMouseDown={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{title}</div>
          <button onClick={onClose} style={styles.iconBtn} aria-label="close">
            ✕
          </button>
        </div>
        <div style={styles.modalBody}>{children}</div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [lang, setLangState] = useState(getLang());
  const [settings, setSettings] = useState(null);
  const [links, setLinks] = useState([]);
  const [carousel, setCarousel] = useState([]);
  const [media, setMedia] = useState([]);
  const [flashItems, setFlashItems] = useState([]);
  const [persons, setPersons] = useState([]);

  // QR modal (for share links)
  const [qrOpen, setQrOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [qrImg, setQrImg] = useState("");
  const [qrBusy, setQrBusy] = useState(false);
  const [qrErr, setQrErr] = useState("");

  // modal states for galleries
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryType, setGalleryType] = useState("image"); // image | video | audio
  const [galleryIndex, setGalleryIndex] = useState(0);

  const photos = useMemo(() => media.filter((m) => m.media_type === "image"), [media]);
  const videos = useMemo(() => media.filter((m) => m.media_type === "video"), [media]);
  const audios = useMemo(() => media.filter((m) => m.media_type === "audio"), [media]);

  useEffect(() => {
    (async () => {
      const [s, n, c, m, f, p] = await Promise.all([
        axios.get(`${API_ROOT}/api/public/site-settings`),
        axios.get(`${API_ROOT}/api/public/nav-links`),
        axios.get(`${API_ROOT}/api/public/carousel`),
        axios.get(`${API_ROOT}/api/public/media`),
        axios.get(`${API_ROOT}/api/public/flash-messages`, { params: { limit: 20 } }),
        axios.get(`${API_ROOT}/api/public/persons`),
      ]);

      setSettings(s.data);
      setLinks(n.data || []);
      setCarousel(c.data || []);
      setMedia(m.data || []);
      setFlashItems(f.data || []);
      setPersons(p.data || []);
    })().catch(console.error);
  }, []);

  function switchLang() {
    const next = lang === "en" ? "kn" : "en";
    setLang(next);
    setLangState(next);
  }

  async function openQrFor(url) {
    if (!url) return;
    try {
      setQrErr("");
      setQrBusy(true);
      setQrUrl(url);
      const dataUrl = await QRCode.toDataURL(url, {
        width: 180,
        margin: 1,
        errorCorrectionLevel: "M",
      });
      setQrImg(dataUrl);
      setQrOpen(true);
    } catch (e) {
      setQrImg("");
      setQrOpen(true);
      setQrErr("Failed to generate QR");
    } finally {
      setQrBusy(false);
    }
  }

  function closeQr() {
    setQrOpen(false);
  }

  function downloadQr() {
    if (!qrImg) return;
    const a = document.createElement("a");
    a.href = qrImg;
    a.download = "share-qr.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const siteName =
    (lang === "kn" ? settings?.site_name_kn : settings?.site_name_en) ||
    settings?.site_name_en ||
    settings?.site_name_kn ||
    "Community Portal";

  const topText =
  (lang === "kn" ? settings?.top_banner_kn : settings?.top_banner_en) ||
  (lang === "kn" ? settings?.top_bar_text_kn : settings?.top_bar_text_en) ||
  "Welcome to our community portal";

  return (
    <div style={styles.page}>
      {/* Top bar */}
      <div style={styles.topbar}>
        <div style={styles.topbarText}>{topText}</div>
        <button onClick={switchLang} style={styles.langBtn}>
          {lang === "en" ? "ಕನ್ನಡ" : "English"}
        </button>
      </div>

      {/* Header / Nav */}
      <div style={styles.navWrap}>
        <div style={styles.navInner}>
          <div style={styles.brand}>{siteName}</div>

          <div style={styles.navLinks}>
            {links.map((l) => (
              <a key={l.id} href={l.url} style={styles.navLink}>
                {lang === "kn" ? l.label_kn || l.label_en : l.label_en || l.label_kn}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={styles.container}>
        {/* Flash Messages (approved only) */}
        <FlashTicker items={flashItems} lang={lang} variant="ticker" />

        {/* Published Profiles with QR share */}
        {persons.length > 0 && (
          <div style={styles.sectionCard}>
            <div style={styles.sectionHeaderRow}>
              <div style={styles.sectionTitle}>{lang === "kn" ? "ಪ್ರೊಫೈಲ್‌ಗಳು" : "Profiles"}</div>
              <div style={styles.sectionHint}>
                {lang === "kn"
                  ? "QR ಸ್ಕ್ಯಾನ್ ಮಾಡಿ ಅಥವಾ ಪ್ರೊಫೈಲ್ ತೆಗೆಯಿರಿ"
                  : "Scan QR or open a profile"}
              </div>
            </div>

            <div style={styles.profileGrid}>
              {persons.map((p) => {
                const name = (lang === "kn" ? p.name_kn : p.name_en) || p.name_en || p.name_kn || p.slug;
                const url = `${window.location.origin}/p/${encodeURIComponent(p.slug)}`;
                return (
                  <div key={p.id || p.slug} style={styles.profileCard}>
                    <div style={styles.profileTop}>
                      <div style={styles.profileAvatar}>
                        {p.photo_url ? (
                          <img alt={name} src={toMediaUrl(p.photo_url)} style={styles.profileAvatarImg} />
                        ) : (
                          <div style={styles.profileAvatarFallback}>{String(name || "P").slice(0, 1).toUpperCase()}</div>
                        )}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={styles.profileName} title={name}>
                          {name}
                        </div>
                        <div style={styles.profileUrl} title={url}>
                          {url}
                        </div>
                      </div>
                    </div>

                    <div style={styles.profileActions}>
                      <a href={url} style={styles.primaryAction}>
                        {lang === "kn" ? "ತೆಗೆಯಿರಿ" : "Open"}
                      </a>
                      <button type="button" onClick={() => openQrFor(url)} style={styles.secondaryAction}>
                        {lang === "kn" ? "QR" : "QR"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Carousel */}
        <div style={styles.card}>
          {carousel.length === 0 ? (
            <div style={styles.empty}>No approved carousel items yet.</div>
          ) : (
            <AdvancedCarousel items={carousel} lang={lang} />
          )}
        </div>

        {/* Galleries */}
        <div style={styles.grid3}>
          <GalleryCard
            title={lang === "kn" ? "ಫೋಟೋ ಗ್ಯಾಲರಿ" : "Photo Gallery"}
            items={photos}
            type="image"
            lang={lang}
            onViewMore={() => {
              setGalleryType("image");
              setGalleryIndex(0);
              setGalleryOpen(true);
            }}
            onOpenItem={(idx) => {
              setGalleryType("image");
              setGalleryIndex(idx);
              setGalleryOpen(true);
            }}
          />

          <GalleryCard
            title={lang === "kn" ? "ವೀಡಿಯೋ ಗ್ಯಾಲರಿ" : "Video Gallery"}
            items={videos}
            type="video"
            lang={lang}
            onViewMore={() => {
              setGalleryType("video");
              setGalleryIndex(0);
              setGalleryOpen(true);
            }}
            onOpenItem={(idx) => {
              setGalleryType("video");
              setGalleryIndex(idx);
              setGalleryOpen(true);
            }}
          />

          <GalleryCard
            title={lang === "kn" ? "ಆಡಿಯೋ ಗ್ಯಾಲರಿ" : "Audio Gallery"}
            items={audios}
            type="audio"
            lang={lang}
            onViewMore={() => {
              setGalleryType("audio");
              setGalleryIndex(0);
              setGalleryOpen(true);
            }}
            onOpenItem={(idx) => {
              setGalleryType("audio");
              setGalleryIndex(idx);
              setGalleryOpen(true);
            }}
          />
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <div style={styles.footerInner}>
          <FooterBlock
            title={lang === "kn" ? "ನಮ್ಮ ಬಗ್ಗೆ" : "About Us"}
            text={lang === "kn" ? settings?.footer_about_kn : settings?.footer_about_en}
          />
          <FooterBlock
            title={lang === "kn" ? "ಸಂಪರ್ಕ" : "Contact"}
            text={
              (settings?.contact_email || "-") +
              (settings?.contact_phone ? ` | ${settings.contact_phone}` : "")
            }
          />
          <FooterBlock
            title={lang === "kn" ? "ವಿಳಾಸ" : "Address"}
text={
  (lang === "kn" ? settings?.contact_address_kn : settings?.contact_address_en) ||
  (lang === "kn" ? settings?.address_kn : settings?.address_en) ||
  "-"
}

          />
          <FooterBlock title={lang === "kn" ? "ಭಾಷೆ" : "Language"} text={lang === "kn" ? "ಕನ್ನಡ / English" : "English / ಕನ್ನಡ"} />
        </div>
        <div style={styles.footerBottom}>
          © {new Date().getFullYear()} {settings?.site_name_en || "Community Portal"}.
        </div>
      </div>

      {/* Gallery Modal */}
      <GalleryModal
        open={galleryOpen}
        type={galleryType}
        lang={lang}
        index={galleryIndex}
        setIndex={setGalleryIndex}
        items={galleryType === "image" ? photos : galleryType === "video" ? videos : audios}
        onClose={() => setGalleryOpen(false)}
      />

      {/* QR Modal */}
      <Modal
        open={qrOpen}
        title={lang === "kn" ? "QR ಕೋಡ್" : "QR Code"}
        onClose={closeQr}
      >
        <div style={styles.qrModalBody}>
          <div style={styles.qrBox}>
            {qrBusy ? (
              <div style={styles.qrStatusText}>Generating…</div>
            ) : qrErr ? (
              <div style={{ ...styles.qrStatusText, color: "#b42318" }}>{qrErr}</div>
            ) : qrImg ? (
              <img src={qrImg} alt="QR" style={styles.qrImg} />
            ) : (
              <div style={styles.qrStatusText}>No QR</div>
            )}
          </div>

          <div style={styles.qrUrlBox}>
            <div style={styles.qrUrlLabel}>URL</div>
            <div style={styles.qrUrlText} title={qrUrl}>
              {qrUrl || "—"}
            </div>
            <div style={styles.qrActions}>
              <button
                type="button"
                onClick={() => {
                  if (!qrUrl) return;
                  navigator.clipboard.writeText(qrUrl);
                }}
                style={styles.secondaryAction}
              >
                {lang === "kn" ? "ಕಾಪಿ" : "Copy"}
              </button>
              <button type="button" onClick={downloadQr} disabled={!qrImg} style={styles.primaryAction}>
                {lang === "kn" ? "ಡೌನ್‌ಲೋಡ್" : "Download"}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ----------------------- CAROUSEL ----------------------- */

function AdvancedCarousel({ items, lang }) {
  const [idx, setIdx] = useState(0);
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, deltaX: 0, w: 0 });

  // guard
  useEffect(() => {
    if (idx >= items.length) setIdx(0);
  }, [items.length, idx]);

  // autoplay
  useEffect(() => {
    if (!items?.length) return;
    const t = setInterval(() => setIdx((v) => (v + 1) % items.length), 4500);
    return () => clearInterval(t);
  }, [items.length]);

  function prev() {
    setIdx((v) => (v - 1 + items.length) % items.length);
  }
  function next() {
    setIdx((v) => (v + 1) % items.length);
  }

  // swipe/drag handlers
  function onDown(e) {
    const el = trackRef.current;
    if (!el) return;
    drag.current.down = true;
    drag.current.startX = "touches" in e ? e.touches[0].clientX : e.clientX;
    drag.current.deltaX = 0;
    drag.current.w = el.getBoundingClientRect().width;
  }

  function onMove(e) {
    if (!drag.current.down) return;
    const x = "touches" in e ? e.touches[0].clientX : e.clientX;
    drag.current.deltaX = x - drag.current.startX;
    // subtle drag feedback
    const el = trackRef.current;
    if (el) {
      el.style.transition = "none";
      const pct = (drag.current.deltaX / drag.current.w) * 100;
      el.style.transform = `translateX(calc(${-idx * 100}% + ${pct}%))`;
    }
  }

  function onUp() {
    if (!drag.current.down) return;
    drag.current.down = false;

    const dx = drag.current.deltaX;
    const w = drag.current.w || 1;
    const threshold = Math.max(40, w * 0.12);

    const el = trackRef.current;
    if (el) {
      el.style.transition = "transform 450ms ease";
      el.style.transform = `translateX(${-idx * 100}%)`;
    }

    if (dx > threshold) prev();
    else if (dx < -threshold) next();
  }

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.style.transition = "transform 450ms ease";
    el.style.transform = `translateX(${-idx * 100}%)`;
  }, [idx]);

  return (
    <div style={styles.carouselWrap}>
      <div
        style={styles.carouselViewport}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onTouchStart={onDown}
        onTouchMove={onMove}
        onTouchEnd={onUp}
      >
        <div ref={trackRef} style={styles.carouselTrack}>
          {items.map((it) => (
            <div key={it.id} style={styles.carouselSlide}>
              <img
                src={toMediaUrl(it.image_url)}
                alt="carousel"
                style={styles.carouselImg}
                draggable={false}
              />
              <div style={styles.carouselTitle}>
                {lang === "kn" ? it.title_kn || it.title_en : it.title_en || it.title_kn}
              </div>
            </div>
          ))}
        </div>

        <button style={{ ...styles.navArrow, left: 12 }} onClick={prev} aria-label="prev">
          ‹
        </button>
        <button style={{ ...styles.navArrow, right: 12 }} onClick={next} aria-label="next">
          ›
        </button>

        {/* Dots */}
        <div style={styles.dots}>
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`slide-${i}`}
              style={{
                ...styles.dot,
                background: i === idx ? "white" : "rgba(255,255,255,0.5)",
              }}
            />
          ))}
        </div>
      </div>

      {/* Thumbnails */}
      <div style={styles.thumbRow}>
        {items.map((it, i) => (
          <button
            key={it.id || i}
            onClick={() => setIdx(i)}
            style={{
              ...styles.thumbBtn,
              outline: i === idx ? "2px solid #0d47a1" : "1px solid #e7eaf0",
            }}
            aria-label={`thumb-${i}`}
          >
            <img src={toMediaUrl(it.image_url)} alt="thumb" style={styles.thumbImg} />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ----------------------- GALLERIES ----------------------- */

function GalleryCard({ title, items, type, lang, onViewMore, onOpenItem }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>{title}</div>

      {items.length === 0 ? (
        <div style={styles.emptySmall}>No approved items.</div>
      ) : (
        <div style={styles.galleryGrid}>
          {items.slice(0, 9).map((m, idx) => (
            <button
              key={m.id || idx}
              onClick={() => onOpenItem(idx)}
              style={styles.thumbTileBtn}
              aria-label={`open-${type}-${idx}`}
            >
              {type === "image" && (
                <img src={toMediaUrl(m.media_url)} alt="media" style={styles.galleryThumb} />
              )}

              {type === "video" && (
                <div style={styles.videoThumbWrap}>
                  <video src={toMediaUrl(m.media_url)} style={styles.galleryThumb} muted />
                  <div style={styles.playBadge}>▶</div>
                </div>
              )}

              {type === "audio" && (
                <div style={styles.audioTile}>
                  <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 6 }}>
                    {lang === "kn" ? m.title_kn || m.title_en : m.title_en || m.title_kn || "Audio"}
                  </div>
                  <div style={{ fontSize: 12, color: "#475569" }}>Tap to play</div>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      <button onClick={onViewMore} style={styles.primaryBtn}>
        {lang === "kn" ? "ಇನ್ನಷ್ಟು ನೋಡಿ" : "View More"}
      </button>
    </div>
  );
}

function GalleryModal({ open, onClose, items, type, lang, index, setIndex }) {
  const i = clamp(index, 0, Math.max(0, items.length - 1));

  useEffect(() => {
    if (!open) return;
    setIndex((v) => clamp(v, 0, Math.max(0, items.length - 1)));
  }, [open, items.length, setIndex]);

  function prev() {
    setIndex((v) => clamp(v - 1, 0, items.length - 1));
  }
  function next() {
    setIndex((v) => clamp(v + 1, 0, items.length - 1));
  }

  // arrow key navigation for image/video
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (type === "audio") return;
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, items.length]);

  const title =
    type === "image"
      ? (lang === "kn" ? "ಫೋಟೋ ಗ್ಯಾಲರಿ" : "Photo Gallery")
      : type === "video"
      ? (lang === "kn" ? "ವೀಡಿಯೋ ಗ್ಯಾಲರಿ" : "Video Gallery")
      : (lang === "kn" ? "ಆಡಿಯೋ ಗ್ಯಾಲರಿ" : "Audio Gallery");

  const current = items[i];

  return (
    <Modal open={open} title={title} onClose={onClose}>
      {items.length === 0 ? (
        <div style={styles.empty}>No items.</div>
      ) : (
        <div style={styles.modalLayout}>
          {/* Viewer */}
          <div style={styles.viewer}>
            {type === "image" && (
              <div style={styles.viewerBox}>
                <img
                  src={toMediaUrl(current.media_url)}
                  alt="full"
                  style={styles.viewerImg}
                />
              </div>
            )}

            {type === "video" && (
              <div style={styles.viewerBox}>
                <video
                  src={toMediaUrl(current.media_url)}
                  controls
                  style={styles.viewerVideo}
                />
              </div>
            )}

            {type === "audio" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontWeight: 900, fontSize: 14 }}>
                  {lang === "kn"
                    ? current.title_kn || current.title_en
                    : current.title_en || current.title_kn || "Audio"}
                </div>
                <audio controls style={{ width: "100%" }}>
                  <source src={toMediaUrl(current.media_url)} />
                </audio>

                <div style={styles.audioList}>
                  {items.map((a, idx) => (
                    <button
                      key={a.id || idx}
                      onClick={() => setIndex(idx)}
                      style={{
                        ...styles.audioRow,
                        background: idx === i ? "#eef2ff" : "white",
                        borderColor: idx === i ? "#c7d2fe" : "#e7eaf0",
                      }}
                    >
                      <div style={{ fontWeight: 800, fontSize: 12 }}>
                        {lang === "kn" ? a.title_kn || a.title_en : a.title_en || a.title_kn || "Audio"}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {idx === i ? "Now playing" : "Play"}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Prev/Next for image/video */}
            {type !== "audio" && (
              <div style={styles.viewerActions}>
                <button onClick={prev} style={styles.secondaryBtn} disabled={i === 0}>
                  Prev
                </button>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {i + 1} / {items.length}
                </div>
                <button onClick={next} style={styles.secondaryBtn} disabled={i === items.length - 1}>
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Thumbnails */}
          {type !== "audio" && (
            <div style={styles.modalThumbs}>
              {items.map((m, idx) => (
                <button
                  key={m.id || idx}
                  onClick={() => setIndex(idx)}
                  style={{
                    ...styles.modalThumbBtn,
                    outline: idx === i ? "2px solid #0d47a1" : "1px solid #e7eaf0",
                  }}
                >
                  {type === "image" ? (
                    <img src={toMediaUrl(m.media_url)} alt="thumb" style={styles.modalThumbImg} />
                  ) : (
                    <div style={{ position: "relative" }}>
                      <video src={toMediaUrl(m.media_url)} style={styles.modalThumbImg} muted />
                      <div style={styles.playBadgeSmall}>▶</div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ----------------------- FOOTER ----------------------- */

function FooterBlock({ title, text }) {
  return (
    <div>
      <div style={{ fontWeight: 900, marginBottom: 8, color: "white" }}>{title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.6, opacity: 0.95 }}>{text || "-"}</div>
    </div>
  );
}

/* ----------------------- STYLES ----------------------- */

const styles = {
  page: {
    fontFamily: "system-ui",
    background: "#f5f7fb",
    minHeight: "100vh",
  },
  topbar: {
    background: "#0d47a1",
    color: "white",
    padding: "10px 14px",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
  },
  topbarText: {
    fontSize: 13,
    lineHeight: 1.3,
  },
  langBtn: {
    background: "white",
    border: 0,
    padding: "6px 10px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 700,
  },
  navWrap: {
    background: "white",
    borderBottom: "1px solid #e7eaf0",
  },
  navInner: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
  },
  brand: {
    fontWeight: 900,
    fontSize: 16,
    color: "#0b1220",
  },
  navLinks: {
    marginLeft: "auto",
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
  },
  navLink: {
    textDecoration: "none",
    color: "#0d47a1",
    fontWeight: 700,
    fontSize: 13,
  },
  container: {
    maxWidth: 1100,
    margin: "16px auto 0",
    padding: "0 14px 18px",
  },
  card: {
    background: "white",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 6px 20px rgba(16,24,40,0.08)",
    padding: 14,
  },
  cardTitle: {
    fontWeight: 900,
    marginBottom: 10,
    fontSize: 14,
  },
  empty: { padding: 12, color: "#64748b" },
  emptySmall: { padding: 8, color: "#64748b", fontSize: 13 },
  grid3: {
    marginTop: 14,
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 14,
  },

  /* Profiles section */
  sectionCard: {
    background: "white",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 6px 20px rgba(16,24,40,0.08)",
    padding: 14,
    marginBottom: 14,
  },
  sectionHeaderRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  sectionTitle: { fontWeight: 900, color: "#0b1220", fontSize: 14 },
  sectionHint: { color: "#667085", fontWeight: 700, fontSize: 13 },
  profileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
  },
   profileCard: {
  border: "1px solid #e7eaf0",
  borderRadius: 14,
  padding: 10,
  background: "#fbfdff",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  flexWrap: "wrap", // allows wrap on mobile
},

  profileTop: { display: "flex", gap: 12, alignItems: "center", minWidth: 0 },
  profileAvatar: {
    width: 46,
    height: 46,
    borderRadius: 12,
    overflow: "hidden",
    background: "#e8eefb",
    display: "grid",
    placeItems: "center",
    flex: "0 0 auto",
  },
  profileAvatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  profileAvatarFallback: { fontWeight: 900, color: "#0b1220" },
  profileName: { fontWeight: 900, color: "#0b1220", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  profileUrl: {
  marginTop: 2,
  fontSize: 12,
  color: "#667085",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
},

  profileActions: {
  display: "flex",
  gap: 8,
  alignItems: "center",
  justifyContent: "flex-end",
  flexWrap: "wrap",
},

primaryAction: {
  textAlign: "center",
  textDecoration: "none",
  background: "#0d47a1",
  color: "white",
  border: 0,
  padding: "8px 14px",
  borderRadius: 10,
  fontWeight: 900,
  cursor: "pointer",
  fontSize: 13,
  minWidth: 110,
},
 
 secondaryAction: {
  background: "white",
  color: "#0d47a1",
  border: "1px solid #d7def0",
  padding: "8px 14px",
  borderRadius: 10,
  fontWeight: 900,
  cursor: "pointer",
  fontSize: 13,
  minWidth: 80,
},


  /* QR modal */
  qrModalBody: { display: "grid", gridTemplateColumns: "1fr", gap: 12 },
  qrBox: {
    display: "grid",
    placeItems: "center",
    border: "1px solid #eef2f7",
    borderRadius: 16,
    padding: 12,
    background: "white",
  },
qrImg: { width: 180, height: 180, maxWidth: "100%", objectFit: "contain", borderRadius: 14 },

  qrStatusText: { fontWeight: 900, color: "#344054" },
  qrUrlBox: { border: "1px solid #eef2f7", borderRadius: 16, padding: 12, background: "#fbfdff" },
  qrUrlLabel: { fontWeight: 900, fontSize: 12, color: "#667085" },
  qrUrlText: { marginTop: 6, fontWeight: 800, color: "#0b1220", wordBreak: "break-all", fontSize: 13 },
  qrActions: { display: "flex", gap: 10, marginTop: 10 },

  // carousel
  carouselWrap: { display: "flex", flexDirection: "column", gap: 10 },
  carouselViewport: {
    position: "relative",
    borderRadius: 12,
    overflow: "hidden",
    background: "#0b1220",
    height: "clamp(160px, 22vw, 300px)",
    userSelect: "none",
    touchAction: "pan-y",
  },
  carouselTrack: {
    height: "100%",
    display: "flex",
    transform: "translateX(0%)",
    transition: "transform 450ms ease",
  },
  carouselSlide: { minWidth: "100%", height: "100%", position: "relative" },
  carouselImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  carouselTitle: {
    position: "absolute",
    left: 12,
    bottom: 12,
    background: "rgba(0,0,0,0.55)",
    color: "white",
    padding: "8px 10px",
    borderRadius: 10,
    fontWeight: 800,
    fontSize: 13,
    maxWidth: "80%",
  },
  navArrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    border: 0,
    width: 42,
    height: 42,
    borderRadius: 999,
    cursor: "pointer",
    background: "rgba(0,0,0,0.4)",
    color: "white",
    fontSize: 24,
    display: "grid",
    placeItems: "center",
  },
  dots: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 10,
    display: "flex",
    justifyContent: "center",
    gap: 6,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    border: 0,
    cursor: "pointer",
  },
  thumbRow: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    paddingBottom: 2,
  },
  thumbBtn: {
    border: 0,
    background: "transparent",
    padding: 0,
    borderRadius: 10,
    cursor: "pointer",
    flex: "0 0 auto",
  },
  thumbImg: {
    width: 86,
    height: 54,
    borderRadius: 10,
    objectFit: "cover",
    display: "block",
  },

  // gallery
  galleryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
    marginBottom: 12,
  },
  thumbTileBtn: {
    border: "1px solid #e7eaf0",
    borderRadius: 12,
    overflow: "hidden",
    padding: 0,
    background: "white",
    cursor: "pointer",
  },
  galleryThumb: {
    width: "100%",
    height: 90,
    objectFit: "cover",
    display: "block",
  },
  videoThumbWrap: { position: "relative" },
  playBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: 999,
    background: "rgba(0,0,0,0.55)",
    color: "white",
    display: "grid",
    placeItems: "center",
    fontSize: 14,
    fontWeight: 900,
  },
  playBadgeSmall: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    borderRadius: 999,
    background: "rgba(0,0,0,0.55)",
    color: "white",
    display: "grid",
    placeItems: "center",
    fontSize: 12,
    fontWeight: 900,
  },
  audioTile: {
    padding: 10,
    height: 90,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  primaryBtn: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 12,
    border: 0,
    background: "#0d47a1",
    color: "white",
    fontWeight: 800,
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #e7eaf0",
    background: "white",
    fontWeight: 800,
    cursor: "pointer",
  },

  // modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.6)",
    display: "grid",
    placeItems: "center",
    padding: 14,
    zIndex: 9999,
  },
  modalCard: {
    width: "min(980px, 100%)",
    background: "white",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 14px",
    borderBottom: "1px solid #e7eaf0",
  },
  modalBody: { padding: 14 },
  iconBtn: {
    border: "1px solid #e7eaf0",
    background: "white",
    width: 34,
    height: 34,
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 900,
  },
  modalLayout: {
    display: "grid",
    gridTemplateColumns: "1.3fr 0.7fr",
    gap: 12,
  },
  viewer: { display: "flex", flexDirection: "column", gap: 10 },
  viewerBox: {
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid #e7eaf0",
    background: "#0b1220",
    minHeight: 320,
    display: "grid",
    placeItems: "center",
  },
  viewerImg: { width: "100%", height: "100%", objectFit: "contain" },
  viewerVideo: { width: "100%", height: "100%", maxHeight: 520 },
  viewerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
  },
  modalThumbs: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxHeight: 520,
    overflowY: "auto",
    paddingRight: 4,
  },
  modalThumbBtn: {
    border: 0,
    background: "transparent",
    padding: 0,
    borderRadius: 12,
    cursor: "pointer",
  },
  modalThumbImg: {
    width: "100%",
    height: 86,
    borderRadius: 12,
    objectFit: "cover",
    display: "block",
    border: "1px solid #e7eaf0",
  },

  audioList: { display: "flex", flexDirection: "column", gap: 8, marginTop: 10 },
  audioRow: {
    padding: 10,
    borderRadius: 12,
    border: "1px solid #e7eaf0",
    textAlign: "left",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
  },

  // footer
  footer: { background: "#0b1220", color: "#cbd5e1", padding: "24px 14px", marginTop: 10 },
  footerInner: {
    maxWidth: 1100,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 16,
  },
  footerBottom: {
    maxWidth: 1100,
    margin: "16px auto 0",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    paddingTop: 12,
    fontSize: 12,
  },
};

/**
 * Responsive adjustments using inline styles:
 * In pure inline-style approach, we can't do media queries directly.
 * So we do a small runtime tweak based on window width.
 * If you already use CSS files, I can provide a CSS version with media queries.
 */
(function attachResponsiveFix() {
  if (typeof window === "undefined") return;
  const apply = () => {
    const w = window.innerWidth;

    // 3 columns -> 1 column on mobile, 2 on tablet
    if (w < 720) {
      styles.grid3.gridTemplateColumns = "1fr";
      styles.footerInner.gridTemplateColumns = "1fr";
      styles.modalLayout.gridTemplateColumns = "1fr";
      styles.viewerBox.minHeight = 240;
      styles.thumbImg.width = 76;
      styles.thumbImg.height = 48;
    } else if (w < 980) {
      styles.grid3.gridTemplateColumns = "repeat(2, minmax(0, 1fr))";
      styles.footerInner.gridTemplateColumns = "repeat(2, minmax(0, 1fr))";
      styles.modalLayout.gridTemplateColumns = "1fr";
      styles.viewerBox.minHeight = 300;
    } else {
      styles.grid3.gridTemplateColumns = "repeat(3, minmax(0, 1fr))";
      styles.footerInner.gridTemplateColumns = "repeat(4, minmax(0, 1fr))";
      styles.modalLayout.gridTemplateColumns = "1.3fr 0.7fr";
      styles.viewerBox.minHeight = 320;
    }
  };

  // apply once + on resize
  apply();
  window.addEventListener("resize", apply);
})();
